import type {
  AdminConversationPage,
  AdminConversationQuery,
  AdminConversationTranscript,
  AdminProjectionFreshness,
  AdminQueryMeta,
} from "../../contracts/admin-read-models";
import { formatAdminDate } from "../../shared/formatting/format-admin-date";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";

export type ConversationHistoryViewModel = {
  generatedAtLabel: string;
  requestId: string;
  freshness: AdminProjectionFreshness;
  totalCount: number;
  query: AdminConversationQuery;
  rows: Array<{
    source: "owner" | "public_pet";
    sourceLabel: string;
    sessionId: string;
    actorUserId: string;
    actorLabel: string;
    targetLabel: string;
    availabilityLabel: string;
    activeTurnStatusLabel: string;
    messageCount: number;
    createdAt: string;
    createdAtLabel: string;
    updatedAt: string;
    updatedAtLabel: string;
  }>;
};

export type ConversationTranscriptViewModel = {
  auditId: string;
  sessionLabel: string;
  messages: Array<{
    messageId: string;
    role: "user" | "assistant";
    roleLabel: string;
    text: string;
    createdAt: string;
    createdAtLabel: string;
  }>;
};

function toFreshness(meta: AdminQueryMeta): AdminProjectionFreshness {
  const values = Object.values(meta.sourceFreshness);
  if (values.includes("stale")) {
    return "stale";
  }
  if (values.includes("partial") || values.includes("unavailable")) {
    return "partial";
  }
  return "fresh";
}

export function formatConversationSourceLabel(
  source: "owner" | "public_pet",
  locale: AdminDashboardLocale,
): string {
  return source === "owner"
    ? pickAdminDashboardLocaleText(locale, { en: "Signed-in owner", ko: "로그인 보호자" })
    : pickAdminDashboardLocaleText(locale, { en: "Public pet", ko: "공개 펫" });
}

export function toConversationHistoryViewModel(
  page: AdminConversationPage,
  meta: AdminQueryMeta,
  query: AdminConversationQuery,
  locale: AdminDashboardLocale,
): ConversationHistoryViewModel {
  return {
    generatedAtLabel: formatAdminDate(meta.generatedAt),
    requestId: meta.requestId,
    freshness: toFreshness(meta),
    totalCount: page.totalCount,
    query,
    rows: page.items.map((row) => ({
      source: row.source,
      sourceLabel: formatConversationSourceLabel(row.source, locale),
      sessionId: row.sessionId,
      actorUserId: row.actorUserId,
      actorLabel: row.actorDisplayName ?? row.actorUserId,
      targetLabel: row.targetDisplayName ?? row.targetPetId ?? "-",
      availabilityLabel: row.availability,
      activeTurnStatusLabel: row.activeTurnStatus ?? "-",
      messageCount: row.messageCount,
      createdAt: row.createdAt,
      createdAtLabel: formatAdminDate(row.createdAt),
      updatedAt: row.updatedAt,
      updatedAtLabel: formatAdminDate(row.updatedAt),
    })),
  };
}

export function toConversationTranscriptViewModel(
  transcript: AdminConversationTranscript,
  locale: AdminDashboardLocale,
): ConversationTranscriptViewModel {
  return {
    auditId: transcript.auditId,
    sessionLabel: `${formatConversationSourceLabel(transcript.session.source, locale)} · ${transcript.session.sessionId}`,
    messages: transcript.messages.map((message) => ({
      messageId: message.messageId,
      role: message.role,
      roleLabel: message.role === "user"
        ? pickAdminDashboardLocaleText(locale, { en: "User", ko: "사용자" })
        : pickAdminDashboardLocaleText(locale, { en: "Assistant", ko: "도우미" }),
      text: message.text,
      createdAt: message.createdAt,
      createdAtLabel: formatAdminDate(message.createdAt),
    })),
  };
}
